import { ViewModel } from "mvvm/ViewModel";
import { JobViewModel } from "./JobViewModel";
import { JobInfo } from "../../../../services/NLIJobService";
import { DashJobService } from "../../../../services/DashJobService";
import { QuestionViewModel } from "./QuestionViewModel";

export type JobListState = Readonly<
    { status: "not-loaded"|"loading"|"updating" } |
    { status: "load-failed", error: string } |
    { status: "update-scheduled"|"idle", timeout: number }>;


export class PageViewModel extends ViewModel {
    constructor(public readonly jobService: DashJobService, public readonly modelAlias: string, private readonly initialJobID?: string) {
        super();
        this.question = new QuestionViewModel(this);
        this._boundUpdateViews = this.updateViews.bind(this);
        this.initialize();
    }

    readonly question: QuestionViewModel;
    private _selectedJob?: JobViewModel;
    private _jobs: readonly JobViewModel[] = [];
    private _jobListState: JobListState = { status: "not-loaded" };
    private _clockSkew: number|null = null;
    private _isSubmitting: boolean = false;
    private readonly _boundUpdateViews: () => void;


    get jobs() {
        return this._jobs;
    }

    private set jobs(value: readonly JobViewModel[]) {
        if (value !== this._jobs) {
            this._jobs = value;
            this.updateViews();
        }
    }

    get jobListState() {
        return this._jobListState;
    }

    private set jobListState(value: JobListState) {
        if (value !== this._jobListState) {
            this._jobListState = value;
            this.updateViews();
        }
    }

    addJob(jobInfo: JobInfo) {
        const job = new JobViewModel(this, jobInfo);
        this.jobs = [job, ...this._jobs];
        this.selectedJob = job;
        // Update execution time with current server time for immediate display
        const currentServerTime = this.getCurrentServerTime();
        job.updateExecutionTime(currentServerTime);
        this.invalidateJobList();
    }

    private getCurrentServerTime(): number {
        return Math.round(Date.now()) + (this._clockSkew ?? 0);
    }

    removeJob(job: JobViewModel) {
        if (job === this._selectedJob && !job.replyTarget) {
            this.selectedJob = undefined;
        }
        this.jobs = this._jobs.filter(j => j !== job);
        this.invalidateJobList();
    }

    get selectedJob() {
        return this._selectedJob;
    }

    set selectedJob(value: JobViewModel|undefined) {
        const previousValue = this._selectedJob;
        if (value !== previousValue) {
            if (previousValue) {
                previousValue.removeView(this._boundUpdateViews);
            }
            if (value) {
                value.addView(this._boundUpdateViews);
            }
            this.question.selectedJob = value;
            this._selectedJob = value;
            if (previousValue && previousValue.isSelected) {
                previousValue.isSelected = false;
            }
            if (value && !value.isSelected) {
                value.isSelected = true;
            }
            this.updateViews();
        }
    }

    private async initialize() {
        let serverTime: number;
        try {
            this.jobListState = { status: "loading" };
            const result = await this.jobService.loadJobs();
            const jobs = result.jobs.map(job => new JobViewModel(this, job));
            this.jobs = jobs;
            serverTime = result.serverTime;

            // If initialJobID is provided, select that job after loading
            if (this.initialJobID) {
                const jobToSelect = jobs.find(j => j.jobID === this.initialJobID);
                if (jobToSelect) {
                    this.selectedJob = jobToSelect;
                }
            }
        } catch (error) {
            this.jobListState = { status: "load-failed", error: this.formatError(error) };
            serverTime = Math.round(Date.now());
        }
        this.scheduleJobUpdates(serverTime);
    }

    async reloadJobs() {
        let serverTime: number;
        const previousSelectedJobID = this._selectedJob?.jobID;
        try {
            this.jobListState = { status: "loading" };
            const result = await this.jobService.loadJobs();
            const existingJobs = new Map(this._jobs.map(job => [job.jobID, job]));
            const jobs = result.jobs.map(jobInfo => {
                const existing = existingJobs.get(jobInfo.jobID);
                if (existing) {
                    existing.updateFromJobInfo(jobInfo);
                    return existing;
                }
                return new JobViewModel(this, jobInfo);
            });
            this.jobs = jobs;
            serverTime = result.serverTime;

            // Preserve selection if the job still exists
            // Since we reuse existing job objects, if the selected job is still in the array,
            // it's the same object and selection is already preserved
            if (previousSelectedJobID) {
                const restoredJob = jobs.find(j => j.jobID === previousSelectedJobID);
                if (restoredJob) {
                    // Only update selection if it's not already the selected job
                    // This prevents unnecessary updates that could cause deselection
                    if (this._selectedJob !== restoredJob) {
                        this.selectedJob = restoredJob;
                    }
                } else {
                    // Job no longer exists, clear selection
                    if (this._selectedJob) {
                        this.selectedJob = undefined;
                    }
                }
            }

            this._jobListInvalidated = false;
        } catch (error) {
            this.jobListState = { status: "load-failed", error: this.formatError(error) };
            serverTime = Math.round(Date.now());
        }
        this.scheduleJobUpdates(serverTime);
    }

    private scheduleJobUpdates(serverTime: number) {
        const pollingInterval = 5000;
        const clientTime = Math.round(Date.now());
        const skew = clientTime - serverTime;
        if (this._clockSkew === null || skew < this._clockSkew) {
            this._clockSkew = skew;
        }
        this.jobs.forEach(job => {
            job.updateExecutionTime(serverTime);
        });
        if (this._timerStopped) {
            return;
        }
        // Two-tier polling — always fire the light Query (returns all
        // in-scope jobs with 5 fields each), then if it surfaces IDs we
        // don't know about, fire a single follow-up loadJobsByIDs to get
        // their full field set so the new rows can render.
        const timeout = window.setTimeout(async () => {
            this._jobListState = { status: "updating" };
            let newServerTime: number;
            try {
                const result = await this.jobService.loadJobStates();
                newServerTime = result.serverTime;

                const responseIDs = new Set(result.jobStates.map(s => s.jobID));
                const knownByID: { [jobID: string]: JobViewModel } = {};
                this.jobs.forEach(j => { knownByID[j.jobID] = j; });

                result.jobStates.forEach(jobState => {
                    const job = knownByID[jobState.jobID];
                    if (job) {
                        job.updateStatus(jobState.status, jobState.alias, jobState.statusMessage, jobState.endTime);
                    }
                });

                const survivors = this.jobs.filter(j => responseIDs.has(j.jobID));
                if (survivors.length !== this.jobs.length) {
                    this.jobs = survivors;
                    if (this._selectedJob && !responseIDs.has(this._selectedJob.jobID)) {
                        this.selectedJob = undefined;
                    }
                }

                const newIDs = result.jobStates
                    .map(s => s.jobID)
                    .filter(jid => !knownByID[jid]);
                if (newIDs.length > 0) {
                    try {
                        const newRows = await this.jobService.loadJobsByIDs(newIDs);
                        const newJobs = newRows.jobs.map(jobInfo => new JobViewModel(this, jobInfo));
                        if (newJobs.length > 0) {
                            this.jobs = [...newJobs, ...this.jobs]
                                .sort((a, b) => b.startTime - a.startTime);
                        }
                    } catch (error) {
                        console.error("Failed to load new jobs", error);
                    }
                }
            } catch (error) {
                console.error("Failed to update job states", error);
                newServerTime = Math.round(Date.now()) + (this._clockSkew ?? 0);
            }
            this.scheduleJobUpdates(newServerTime);
        }, pollingInterval);
        this.jobListState = { status: "update-scheduled", timeout };
    }

    private _jobListInvalidated = false;
    private _timerStopped = false;

    invalidateJobList() {
        this._jobListInvalidated = true;
    }

    get jobListInvalidated() {
        return this._jobListInvalidated;
    }

    stopTimer() {
        this._timerStopped = true;
    }

    get isSubmitting() {
        return this._isSubmitting;
    }

    set isSubmitting(value: boolean) {
        if (this._isSubmitting !== value) {
            this._isSubmitting = value;
            this.updateViews();
        }
    }
}